import { FlexColumn } from "Routes/styled";
import styled, { css } from "styled-components";
import { Card } from "melodies-source/Card";
import { DateTime } from "luxon";
import { Button } from "melodies-source/Button";
import { svg } from "svg";
import { useDesktopMediaQuery, useEventEntries } from "hooks";
import { SetLiveEvent } from "models/event";
import RecordingIcon from "Components/RecordingIcon";
import { Body2, H2, H3, Subtitle1 } from "melodies-source/Text";
import { useMobileMediaQuery } from "hooks/useMobileMediaQuery";
import { AdditionalGroupsAvatars } from "Components/AdditionalGroups";
import { formatNumber } from "Utils/format";
import { CalendarDisplay } from "Components/Calendar";

export interface EventCardBaseProps {
  event: SetLiveEvent;
  showType: "upcoming" | "past";
  live?: boolean;
  actions?: React.ReactNode;
  menu?: React.ReactNode;
  statusBadge?: React.ReactNode;
  children?: React.ReactNode;
  hideCalendar?: boolean;
}

export const EventCardBase: React.FC<EventCardBaseProps> = ({
  event,
  showType,
  live,
  actions,
  menu,
  statusBadge,
  children,
  hideCalendar,
}) => {
  const isDesktop = useDesktopMediaQuery();
  const isMobile = useMobileMediaQuery();
  const { entriesTotals } = useEventEntries(event.id, event.version);

  const totalEntries = entriesTotals?.all ?? entriesTotals?.inVenue ?? 0;
  const multiAccount = !!event.additionalGroups?.length;

  const Venue = isMobile ? H3 : H2;
  const Info = isMobile ? Body2 : H3;

  const entriesText = `${formatNumber(totalEntries)} ${
    totalEntries === 1 ? "Entry" : "Entries"
  }`;

  const eventDateTime = DateTime.fromMillis(event.startsAt, {
    zone: event.timeZoneName,
  });

  const timeDisplay =
    showType === "upcoming"
      ? eventDateTime.toFormat("h:mm a ZZZZ")
      : eventDateTime.toLocaleString(DateTime.DATE_MED);

  const location = event.displayAddress || event.address;
  const showInlineEntries = !isMobile && !live;
  const showEntriesBelow = live && !isMobile;
  const showEntriesAsMobileSubtitle = isMobile && showType === "past";

  return (
    <EventCardBaseContainer isElevated={!live} live={live}>
      {menu}
      {showType === "upcoming" && !hideCalendar && (
        <StyledCalendar date={eventDateTime} />
      )}
      {!live && showType === "past" && <StyledMapPin />}
      {live && !isDesktop && (
        <MobileLiveNow label={<LiveNowText>Live Now</LiveNowText>} />
      )}
      <FlexColumn>
        <Venue>{event.venue}</Venue>
        <Info>
          {timeDisplay} • {location}
          {showInlineEntries && ` • ${entriesText}`}
          {showEntriesBelow && (
            <>
              <br />
              {entriesText}
            </>
          )}
          {showEntriesAsMobileSubtitle && <Subtitle1>{entriesText}</Subtitle1>}
        </Info>
        {statusBadge}
      </FlexColumn>
      <RightSideContainer>
        {live && isDesktop && (
          <DesktopLiveNow label={<LiveNowText>Live Now</LiveNowText>} />
        )}
        {multiAccount && (
          <AdditionalGroups>
            <AdditionalGroupsAvatars ids={event.additionalGroups} />
          </AdditionalGroups>
        )}
        <ActionButtonContainer>{actions}</ActionButtonContainer>
      </RightSideContainer>
      {children}
    </EventCardBaseContainer>
  );
};

// Shared styled components
export const MenuContainer = styled.div`
  position: absolute;
  top: 3px;
  right: 5px;
`;

interface ActionButtonProps {
  live?: boolean;
  multiAccount?: boolean;
}

const ActionButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  flex: 1 1 auto;
  margin: 16px 0 0 0;

  ${({ theme }) => theme.media.desktop} {
    margin: 0 0 0 32px;
  }
  ${({ theme }) => theme.media.mobile} {
    button {
      min-width: 110px;
    }
  }
`;

export const ActionButton = styled(Button)<ActionButtonProps>`
  ${({ live, multiAccount }) => !live && !multiAccount && "margin-left: auto"};

  ${({ theme }) => theme.media.mobile} {
    margin: 15px 0 0 0;
  }
`;

export const RightSideContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex: 1 0 0;
  width: 100%;

  ${(p) => p.theme.media.mobile} {
    flex-direction: column;
  }
`;

const DesktopLiveNow = styled(RecordingIcon)`
  flex: 1 1 100%;
  justify-content: center;
  padding: 0 20px 0 24px;
  ${({ theme }) => theme.media.maxTablet} {
    padding: 0 16px 0 16px;
  }
`;

const MobileLiveNow = styled(RecordingIcon)`
  margin-bottom: 5px;
  margin-left: auto;
  margin-right: auto;
`;

const LiveNowText = styled.div`
  font-weight: 700;
  color: var(--text-color);
  font-size: 30px;
  line-height: 45px;
  font-family: Poppins;
  white-space: nowrap;

  ${({ theme }) => theme.media.maxTablet} {
    font-size: 24px;
    line-height: 36px;
  }
`;

const StyledCalendar = styled(CalendarDisplay)`
  width: 86px;
  height: 100%;
  ${({ theme }) => theme.media.desktop} {
    margin-right: 22px;
  }

  ${({ theme }) => theme.media.mobile} {
    margin: 15px 0 10px;
    width: 70px;
    height: 100%;
  }
`;

const StyledMapPin = styled(svg.MapPinIcon)`
  margin-right: 17px;

  ${({ theme }) => theme.media.mobile} {
    width: 75px;
    height: 75px;
    margin: 15px 0 20px;
  }
`;

interface EventCardBaseContainerProps {
  live?: boolean;
}

export const EventCardBaseContainer = styled(Card)<EventCardBaseContainerProps>`
  display: flex;
  align-items: center;
  width: 100%;
  border-radius: 12px;
  padding: 33px 38px 32px;
  position: relative;
  border: none;

  ${({ live }) =>
    live && "background: var(--live-event-card-background-color)"};

  ${({ live }) =>
    !live &&
    css`
      ${({ theme }) => theme.media.desktop} {
        box-shadow: 0px 0px 25px var(--box-shadow-color);
      }
    `};

  ${({ theme }) => theme.media.mobile} {
    flex-direction: column;
    align-items: center;
    padding: 14px 20px 25px 20px;

    & > ${FlexColumn} {
      text-align: center;
    }
  }

  ${H2}, ${H3} {
    color: var(--text-color);
  }

  ${Body2} {
    color: var(--text-color);
  }

  ${Subtitle1} {
    margin: 5px 0;
  }
`;

const AdditionalGroups = styled.div`
  margin: 0 20px 0;

  ${(p) => p.theme.media.mobile} {
    margin: 15px 0 0 0;
  }
`;
